import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import TopicGroupLoader from './TopicGroupLoader';
import TopicGroupSaver from './TopicGroupSaver';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import userStateEngine2 from '../../../../controllers/UserState/UserStateEngine2';
import _cache from './TopicGroupCache';
import _ from 'lodash';
import { UserStateKeys } from '../../../../controllers/UserState/UserStateKeys';
import { subscribe } from '../../../../services/utils/pubsub';
import { EngineEvents } from '../../../../services/wss/events/EngineEvents';
import { createHandlers } from './TopicGroupEngineHandlerFactory';
import groupNodeEngine from '../Engines/GroupNodeEngine';

const _name = 'TopicGroupEngine';

class TopicGroupEngine extends EngineCleanupBase {
    _loader = new TopicGroupLoader();
    _saver = new TopicGroupSaver();
    _selected = null;
    _handlers = null;
    _unsubscribe = null;

    constructor() {
        super();

        this._handlers = createHandlers(_cache, this._saver);
    }

    // ─── lifecycle ────────────────────────────────────────

    bootup = () => {
        this.initialize(_name);

        _cache.init();

        if (this._unsubscribe) this._unsubscribe();
        this._unsubscribe = subscribe(EngineEvents.channel, this._onEvent);
    };

    clear = () => {
        _cache.clear();
        this._selected = null;
        super.clear();
    };

    destroy = () => {
        _cache.clear();
        if (this._unsubscribe) this._unsubscribe();
    };

    // ─── public ───────────────────────────────────────────

    fetch = async () => {
        const groups = await this._loader.load();
        if (groups === null) return _cache.clear();
        _cache.setGroups(groups);
        this._resolveSelected();
        this._notify();
    };

    save = async (form) => {
        const ok = await this._saver.save(form);
        if (!ok) return;

        const updated = _cache.updateGroup(form);
        if (!updated) return;
        this._notify();
    };

    getGroups = () => _.orderBy(_cache.getGroups(), ['name'], ['asc']);

    hasGroups = () => _cache.getCount() > 0;

    /**
     * Persists the selected group to UserState and updates internal state.
     * @param {object} group
     */
    selectGroup = async (group) => {
        if (!group) {
            return;
        }

        try {
            this._selected = group;
            await userStateEngine2.set([{ name: UserStateKeys.yourNoteActiveTopicId, value: group.rk }]);
            this._notify();
        } catch (error) {
            this.logError('selectGroup', error.message);
        }
    };

    /**
     * Returns the current selected group.
     * @returns {object|null}
     */
    selectedGroup = () => this._selected;

    /**
     * Returns group by rk — used for edits.
     * @param {string} rk
     * @returns {object|null}
     */
    getGroup = (rk) => {
        return _cache.getGroups().find((g) => g.rk === rk) ?? null;
    };

    // ─── called by PageContent ────────────────────────────

    /**
     * Called by PageContent on OnGroupNodesLoaded SignalR event.
     * Updates activeNodeId + activeEntryId on the group in cache.
     * @param {object} data - { gid, activeNodeId, activeEntryId, nodes }
     */
    onGroupNodesLoaded = (data) => {
        if (!data?.gid) return;
        _cache.setActiveEntry({ rk: data.gid, nid: data.activeNodeId, eid: data.activeEntryId });
    };

    onGroupsRecreated = (items) => {
        _cache.setGroups(items);
        this._resolveSelected();
        this._notify();
    };

    onGroupChanged = (group) => {
        const updated = _cache.updateGroup(group);
        if (!updated) {
            return;
        }

        this._notify();
    };

    onGroupsMetaDataUpdated = (data) => {
        try {
            let updated = false;
            data.data.forEach((x) => {
                // the group that was modified
                const group = _cache.get(x.gid);
                if (!group) {
                    // this.logError('_onEvent', error.message);
                    return;
                }

                group.total = x.total;
                updated = true;
            });

            if (!updated) {
                return;
            }

            this._notify();
        } catch (error) {
            this.logError('_onEvent', error.message);
        }
    };

    onGroupCreated = (items) => {
        const list = Array.isArray(items) ? items : [items];

        list.forEach((g) => _cache.addGroup(g));
        this._notify();
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Resolves selected group from cache based on UserState — fallback to first group.
     */
    _resolveSelected = () => {
        const rk = userStateEngine2.getValue(UserStateKeys.yourNoteActiveTopicId);
        const groups = _cache.getGroups();
        const found = groups.find((g) => g.rk === rk) ?? groups[0] ?? null;
        this._selected = found;
        this._notify();
    };

    /**
     * Handles engine state events from EngineEvents.channel.
     * Routes to the correct handler based on e.name.
     */
    _onEvent = (e) => {
        try {
            if (e.target !== _name) return;
            this._handlers[e.name]?.handle(e.data);
        } catch (error) {
            this.logError('_onEvent', error.message);
        }
    };

    _notify = (event = 'groupsChanged') => {
        const data = this._getEventData(event);
        ComponentNotifier.notify(_name, event, data);
    };

    _getEventData = (event) => {
        if (event === 'groupsChanged') {
            return {
                groups: this.getGroups(),
                selected: this._selected,
            };
        }

        if (event === 'groupSaved') {
            const data = {
                active: groupNodeEngine.getActiveNode(),
                nodes: groupNodeEngine.getNodes(),
                selectedEntry: groupNodeEngine.getSelectedEntry(),
            };

            return data;
        }
    };
}

const topicGroupEngine = new TopicGroupEngine();
export default topicGroupEngine;
